import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { asMoney, forTemplate, localizeCount } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import type { BillingIds, FreeTrialTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { getOrganizationBillingUrl } from '@trello/urls';

import { useHasReverseTrialExperience } from 'app/src/components/FreeTrial';
import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';
import { PlanComparisonContext } from '../PlanComparisonContext';
import type { BillingQuotes, ChangeStepFn } from '../types';

import styles from './PlanComparisonPremiumHeaderCell.less';

const format = forTemplate('billing_page_one');

const getActionPrompt = (
  isReverseTrial: boolean,
  isModal: boolean,
  isEligibleForTrial: boolean,
  isMonthly: boolean,
  onClickFreeTrialButton: () => void,
  onClickUpgradeButton: () => void,
  teamName: string,
  hasEndOfTrialFrictionExperience?: boolean,
  onKeepPremium?: () => void,
) => {
  if (isReverseTrial) {
    return (
      <Button
        appearance="primary"
        href={getOrganizationBillingUrl(teamName, { selected: 'premium' })}
      >
        {format('upgrade-now')}
      </Button>
    );
  }
  if (hasEndOfTrialFrictionExperience) {
    return (
      <Button appearance="primary" onClick={onKeepPremium}>
        {'Keep Premium'}
      </Button>
    );
  }
  if (isModal) {
    return (
      <section className={styles.currentPlan}>
        <div>{format('current-plan')}</div>
        <div>({format(isMonthly ? 'paid-monthly' : 'paid-yearly')})</div>
      </section>
    );
  }

  if (isEligibleForTrial) {
    return (
      <>
        <Button
          key="free-trial-business-class"
          onClick={onClickFreeTrialButton}
          appearance="primary"
          testId={getTestId<FreeTrialTestIds>('active-free-trial-banner')}
        >
          <WithEnterpriseManagedOverride>
            {localizeCount('try free', 14)}
          </WithEnterpriseManagedOverride>
        </Button>
        <p className={styles.purchase}>
          {format('or', {
            purchaseNow: (
              <Button
                key={`upgrade-business-class`}
                testId={getTestId<BillingIds>('upgrade-team-bc-button')}
                appearance="link"
                onClick={onClickUpgradeButton}
              >
                {format('upgrade-now-lower-case')}
              </Button>
            ),
          })}
        </p>
      </>
    );
  }
  return (
    <Button
      key="upgrade-business-class"
      onClick={onClickUpgradeButton}
      appearance="primary"
      testId={getTestId<BillingIds>('upgrade-team-button')}
    >
      {format('upgrade-now')}
    </Button>
  );
};

export interface PlanComparisonPremiumHeaderCellProps {
  teamId: string;
  teamName: string;
  isMonthly: boolean;
  // Business class is a legacy name for the premium plan!
  prices?: BillingQuotes['businessPrices'];
  onShowFreeTrialModal: (planName: string) => void;
  changeStep: ChangeStepFn;
  isEligibleForTrial?: boolean;
  isInPPCohort?: boolean;
}

export const PlanComparisonPremiumHeaderCell: FunctionComponent<
  PlanComparisonPremiumHeaderCellProps
> = ({
  teamId,
  teamName,
  isMonthly,
  prices,
  onShowFreeTrialModal,
  changeStep,
  isEligibleForTrial,
  isInPPCohort,
}) => {
  const { isModal, hasEndOfTrialFrictionExperience, onKeepPremium } =
    useContext(PlanComparisonContext);

  const productCode = isMonthly
    ? prices?.monthly.productCode
    : prices?.annual.productCode;
  const pricePerUser = isMonthly
    ? prices?.monthly.subTotalPerUser
    : prices?.annual.subTotalPerUser;

  const isReverseTrial = useHasReverseTrialExperience();

  const onKeepPremiumWithAnalytics = useCallback(() => {
    if (hasEndOfTrialFrictionExperience) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'choosePremiumButton',
        source: 'trelloPlanPricingModal',
        attributes: {
          viaEndOfTrial: true,
        },
      });
    }
    onKeepPremium?.();
  }, [hasEndOfTrialFrictionExperience, onKeepPremium]);

  const sendClickUpgradeButtonEvent = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'upgradeToBCButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
  };

  const onClickFreeTrialButton = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'startBCFreeTrialButton',
      source: 'planComparisonSection',
      containers: {
        organization: {
          id: teamId,
        },
      },
      attributes: {
        eligibleForFreeTrial: isEligibleForTrial,
      },
    });
    onShowFreeTrialModal('business class');
  };

  const onClickUpgradeButton = () => {
    changeStep({
      selectedProductCode: productCode || null,
      step: 'billing',
      analyticsCb: sendClickUpgradeButtonEvent,
    });
  };

  return (
    <th
      className={styles.planCell}
      data-value={format('best-value')}
      data-testid={getTestId<BillingIds>('plan-comparison-cell-premium')}
    >
      <h3 className={styles.name}>{format('business-class-name')}</h3>
      <p className={styles.info}>
        {isInPPCohort
          ? format('premium-description-personal-productivity')
          : format('premium-description-repackaging-gtm')}
      </p>
      <span className={styles.price}>
        {format('amount-usd', {
          amount: asMoney(pricePerUser || 0).replace(/\.00$/, ''),
        }).replace(/\s/g, ' ')}
      </span>
      <p className={styles.frequency}>
        {format('per-user')}
        <br />
        {format(isMonthly ? 'per-month' : 'per-year')}
      </p>
      {getActionPrompt(
        isReverseTrial,
        Boolean(isModal),
        Boolean(isEligibleForTrial),
        isMonthly,
        onClickFreeTrialButton,
        onClickUpgradeButton,
        teamName,
        hasEndOfTrialFrictionExperience,
        onKeepPremiumWithAnalytics,
      )}
    </th>
  );
};
